// An aircraft profile: one JSON document holding everything the planner and
// the weight & balance sheet need to know about an airframe, so an aircraft
// can be configured by importing a file and handed on by exporting one. The
// performance block is exactly flight.aircraft_performance's contract
// (AircraftPerformanceSchema); the aircraft block is the few flight.aircraft
// columns the loading sheet reads. The aircraft-profile skill writes these
// files.
//
// Pure; see weightBalance.ts.

import { z } from 'zod'
import { FUEL_TYPES } from './constants'
import { AircraftPerformanceSchema, type AircraftPerformanceInput } from './plan'

export const AIRCRAFT_PROFILE_FORMAT = 'jupi-aircraft-profile'
export const AIRCRAFT_PROFILE_VERSION = 1

export const AircraftProfileSchema = z.object({
  format: z.literal(AIRCRAFT_PROFILE_FORMAT),
  version: z.literal(AIRCRAFT_PROFILE_VERSION),
  aircraft: z.object({
    registration: z.string().trim().toUpperCase().min(1).max(10),
    aircraftType: z.string().trim().max(100).nullable().optional(),
    fuelType: z.enum(FUEL_TYPES),
    fuelConsumptionUsg: z.number().positive().max(1000).nullable().optional(),
    mtowKg: z.number().positive().max(20000).nullable().optional(),
  }),
  performance: AircraftPerformanceSchema,
})
export type AircraftProfile = z.infer<typeof AircraftProfileSchema>

export interface ProfileAircraft {
  registration: string
  aircraftType: string | null
  fuelType: string
  fuelConsumptionUsg: number | null
  mtow: number | null
}

/** The profile of `aircraft`, ready to be written as a file. */
export function buildAircraftProfile(aircraft: ProfileAircraft, performance: AircraftPerformanceInput): AircraftProfile {
  return AircraftProfileSchema.parse({
    format: AIRCRAFT_PROFILE_FORMAT,
    version: AIRCRAFT_PROFILE_VERSION,
    aircraft: {
      registration: aircraft.registration,
      aircraftType: aircraft.aircraftType,
      fuelType: aircraft.fuelType,
      fuelConsumptionUsg: aircraft.fuelConsumptionUsg,
      mtowKg: aircraft.mtow,
    },
    // The engine rating and timestamps stay out: they are not the editor's to set.
    performance: AircraftPerformanceSchema.parse(performance),
  })
}

export type ParsedProfile = { ok: true; profile: AircraftProfile } | { ok: false; errors: string[] }

/** `text` as a profile, or what is wrong with it, one line per problem. */
export function parseAircraftProfile(text: string): ParsedProfile {
  let json: unknown
  try {
    json = JSON.parse(text)
  } catch {
    return { ok: false, errors: ['Not valid JSON'] }
  }
  const result = AircraftProfileSchema.safeParse(json)
  if (result.success) return { ok: true, profile: result.data }
  return { ok: false, errors: result.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`) }
}

/** The profile as the text of a file; stable key order so diffs are readable. */
export function stringifyAircraftProfile(profile: AircraftProfile): string {
  return `${JSON.stringify(profile, null, 2)}\n`
}

/** Seat rows the aircraft has, for drawing its layout: one without rear seats, else two. */
export function seatRows(performance: { hasRearSeats?: boolean }): 1 | 2 {
  return performance.hasRearSeats === false ? 1 : 2
}
